import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import StatusBadge from '../../../components/shared/StatusBadge';
import { formatDate, formatCurrency } from '../../../utils/formatters';
import { Filter, Plus, ChevronRight, Trash2 } from 'lucide-react';

const STATUSES = ['', 'Recebido', 'Em Produção', 'Pronto', 'Em Entrega', 'Entregue', 'Cancelado'];
const TURNOS = ['', 'Manhã', 'Tarde'];
const PRAZOS = ['', '24h', '48h', '72h'];

export default function PedidosLista() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [pedidos, setPedidos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', turno: '', prazo: '', data_inicio: '', data_fim: '', search: '' });
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPedidos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 30 });
      if (filters.status) params.set('status', filters.status);
      if (filters.turno) params.set('turno', filters.turno);
      if (filters.prazo) params.set('prazo', filters.prazo);
      if (filters.data_inicio) params.set('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.set('data_fim', filters.data_fim);
      if (filters.search) params.set('search', filters.search);
      const res = await api.get(`/pedidos?${params}`);
      setPedidos(res.data.pedidos || []);
      setTotal(res.data.total || 0);
    } catch (err) {} finally { setLoading(false); }
  };

  useEffect(() => { loadPedidos(); }, [filters, page]);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/pedidos/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadPedidos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir pedido');
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="pedidos-lista">
      {/* Confirm Delete Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-dashboard font-bold text-lg text-brand-text mb-2">Confirmar exclusão</h3>
            <p className="text-sm font-body text-brand-text-muted mb-5">
              Excluir permanentemente o pedido <strong className="text-brand-text">#{deleteTarget.label}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 rounded-lg border border-brand-border text-sm font-dashboard text-brand-text-muted hover:bg-brand-bg" data-testid="cancel-delete-btn">Cancelar</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-dashboard font-semibold hover:bg-red-700 disabled:opacity-60" data-testid="confirm-delete-btn">{deleting ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="dashboard-heading text-2xl">Pedidos</h1>
          <p className="text-brand-text-muted font-body text-sm">{total} pedido(s) encontrado(s)</p>
        </div>
        <Link
          to="/admin/pedidos/novo"
          className="flex items-center gap-2 px-4 py-2 rounded-lg btn-primary text-sm font-dashboard font-semibold"
          data-testid="novo-pedido-btn"
        >
          <Plus className="w-4 h-4" />
          Novo Pedido
        </Link>
      </div>

      {/* Filters */}
      <div className="card-dashboard mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <input
            className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body col-span-2 md:col-span-1"
            placeholder="Buscar cliente..."
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
            data-testid="filter-search-cliente"
          />
          <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={filters.status} onChange={e => setFilter('status', e.target.value)} data-testid="filter-status">
            <option value="">Todos status</option>
            {STATUSES.filter(Boolean).map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={filters.turno} onChange={e => setFilter('turno', e.target.value)}>
            <option value="">Todos turnos</option>
            {TURNOS.filter(Boolean).map(t => <option key={t}>{t}</option>)}
          </select>
          <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={filters.prazo} onChange={e => setFilter('prazo', e.target.value)}>
            <option value="">Todos prazos</option>
            {PRAZOS.filter(Boolean).map(p => <option key={p}>{p}</option>)}
          </select>
          <input type="date" className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={filters.data_inicio} onChange={e => setFilter('data_inicio', e.target.value)} data-testid="filter-data-inicio" />
          <input type="date" className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={filters.data_fim} onChange={e => setFilter('data_fim', e.target.value)} />
        </div>
      </div>

      {/* Table */}
      <div className="card-dashboard overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : pedidos.length === 0 ? (
          <p className="text-center text-brand-text-muted text-sm py-12 font-body">Nenhum pedido encontrado</p>
        ) : (
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="border-b border-brand-border bg-brand-bg">
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Pedido</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Cliente</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Status</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Prazo</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Turno</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Data</th>
                {isAdmin && <th className="text-right py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Total</th>}
                <th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map(p => (
                <tr key={p.id} className="border-b border-brand-border last:border-0 hover:bg-brand-bg/50">
                  <td className="py-2.5 px-3 font-semibold text-brand-primary">#{p.numero}</td>
                  <td className="py-2.5 px-3 text-brand-text">{p.cliente_nome || '–'}</td>
                  <td className="py-2.5 px-3"><StatusBadge status={p.status} /></td>
                  <td className="py-2.5 px-3 text-brand-text-muted">{p.prazo}</td>
                  <td className="py-2.5 px-3 text-brand-text-muted">{p.turno || '–'}</td>
                  <td className="py-2.5 px-3 text-brand-text-muted">{formatDate(p.data_pedido)}</td>
                  {isAdmin && <td className="py-2.5 px-3 text-right font-semibold">{p.total != null ? formatCurrency(p.total) : '–'}</td>}
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/pedidos/${p.id}`} className="text-brand-primary hover:text-brand-dark" data-testid="ver-pedido-btn">
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                      {isAdmin && (
                        <button
                          onClick={() => setDeleteTarget({ id: p.id, label: p.numero })}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Excluir pedido"
                          data-testid="delete-pedido-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Pagination */}
        {total > 30 && (
          <div className="flex items-center justify-center gap-3 pt-4 border-t border-brand-border">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-brand-border text-sm disabled:opacity-40 font-body">Anterior</button>
            <span className="text-sm font-body text-brand-text-muted">Página {page}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={pedidos.length < 30} className="px-3 py-1.5 rounded-lg border border-brand-border text-sm disabled:opacity-40 font-body">Próximo</button>
          </div>
        )}
      </div>
    </div>
  );
}
